import { AccountModule } from '@/core/modules/account/account.module'
import { FraudDefense } from '@/ui/routes/fraud-defense'
import { FraudDefenseConfigurations as ConfigurationsPage } from '@/ui/routes/fraud-defense/routes/configurations'
import { FraudDefenseOpenIssues as OpenIssuesPage } from '@/ui/routes/fraud-defense/routes/open-issues'
import { Integrations as IntegrationsRoute } from '@/ui/routes/integrations'
import { NewInvitationPage } from '@/ui/routes/new-invitation'
import { SettingsAccountPage } from '@/ui/routes/settings/routes/account'
import '@assets/styles/index.scss'
import { ROUTES } from '@core/config/routing'
import AuthLayout from '@layouts/authentication/auth.layout'
import DashboardLayout from '@layouts/dashboard/dashboard.layout'
import LoginRoute from '@routes/authentication/login/login.route'
import LogoutRoute from '@routes/authentication/logout/logout.route'
import AssetsRoot from '@routes/digital-footprint/assets.route'
import UsersRoute from '@routes/users/users.route'
import DevicesRoute from '@routes/devices/devices.route'

import DomainsRoute from '@routes/digital-footprint/routes/domains/domains.route'
import TechnologiesRoute from '@routes/digital-footprint/routes/technologies/technologies.route'
import { Alerts as AlertsPage } from '@routes/mdr/routes/alerts'
import { Incidents as IncidentsPage } from '@routes/mdr/routes/incidents'
import { ServiceRequests as ServiceRequestsPage } from '@routes/mdr/service-requests'
import SettingsTeamRoute from '@routes/settings/routes/team/settings.team.route'
import SettingsUserRoute from '@routes/settings/routes/user/settings.user.route'
import SettingsRoute from '@routes/settings/settings.route'
import SummaryRoute from '@routes/summary/summary.route'
import NotFoundRoute from '@ui/routes/service-pages/404'
import { Navigate, Route, Routes } from 'react-router-dom'
import MdrPage from '@routes/mdr'
import { Overview } from '@/ui/routes/mdr/routes/overview'
import { IntegrationsStore } from '@/core/store/Integrations'
import { useMemo } from 'react'

const MainRouter = () => {
    const { accountType } = AccountModule.useStore()

    const isGoogleIntegrationActive =
        IntegrationsStore.isIntegrationActiveByType('google_fraud_defense')
    const isMsIntegrationActive =
        IntegrationsStore.isIntegrationActiveByType('ms_fraud_defense')

    const isFdIntegrationEnabled =
        isGoogleIntegrationActive || isMsIntegrationActive

    const isMdrAccountType = accountType === 'MDR'

    const fraudDefenseRoute = useMemo(() => {
        return (
            <Route path={ROUTES.fraud_defense.path} element={<FraudDefense />}>
                <Route
                    path={ROUTES.fraud_defense.path}
                    element={
                        <Navigate
                            to={ROUTES.fraud_defense.children.open_issues.path}
                        />
                    }
                />
                <Route
                    path={ROUTES.fraud_defense.children.open_issues.path}
                    element={<OpenIssuesPage />}
                />
                <Route
                    path={ROUTES.fraud_defense.children.configurations.path}
                    element={<ConfigurationsPage />}
                />
            </Route>
        )
    }, [isFdIntegrationEnabled])

    const mdrRoute = useMemo(() => {
        return (
            <Route path={ROUTES.mdr.path} element={<MdrPage />}>
                <Route
                    path={ROUTES.mdr.path}
                    element={
                        <Navigate to={ROUTES.mdr.children.overview.path} />
                    }
                />
                <Route
                    path={ROUTES.mdr.children.alerts.path}
                    element={<AlertsPage />}
                />
                <Route
                    path={ROUTES.mdr.children.incidents.path}
                    element={<IncidentsPage />}
                />
                <Route
                    path={ROUTES.mdr.children.service_requests.path}
                    element={<ServiceRequestsPage />}
                />
                <Route
                    path={ROUTES.mdr.children.overview.path}
                    element={<Overview />}
                />
            </Route>
        )
    }, [])

    if (isMdrAccountType) {
        return (
            <Routes>
                <Route key={1}>
                    <Route
                        path={ROUTES.new_invitation.path}
                        element={<NewInvitationPage />}
                    />
                </Route>
                <Route key={2} element={<AuthLayout />}>
                    <Route
                        path={ROUTES.auth.children.login.path}
                        element={<LoginRoute />}
                    />
                    <Route
                        path={ROUTES.auth.children.logout.path}
                        element={<LogoutRoute />}
                    />
                </Route>
                <Route key={3} element={<DashboardLayout />}>
                    <Route
                        path={ROUTES.summary.path}
                        element={<Navigate to={ROUTES.mdr.path} />}
                    />
                    {mdrRoute}
                    <Route
                        path={ROUTES.settings.path}
                        element={<SettingsRoute />}
                    >
                        <Route
                            path={ROUTES.settings.path}
                            element={
                                <Navigate
                                    to={ROUTES.settings.children.account.path}
                                />
                            }
                        />
                        <Route
                            path={ROUTES.settings.children.account.path}
                            element={<SettingsAccountPage />}
                        />
                        <Route
                            path={ROUTES.settings.children.user.path}
                            element={<SettingsUserRoute />}
                        />
                        <Route
                            path={ROUTES.settings.children.team.path}
                            element={<SettingsTeamRoute />}
                        />
                    </Route>
                </Route>
                <Route key={4} path="*" element={<NotFoundRoute />} />,
            </Routes>
        )
    }

    return (
        <Routes>
            <Route key={1}>
                <Route
                    path={ROUTES.new_invitation.path}
                    element={<NewInvitationPage />}
                />
            </Route>
            <Route key={2} element={<AuthLayout />}>
                <Route
                    path={ROUTES.auth.children.login.path}
                    element={<LoginRoute />}
                />
                <Route
                    path={ROUTES.auth.children.logout.path}
                    element={<LogoutRoute />}
                />
            </Route>
            <Route key={3} element={<DashboardLayout />}>
                <Route path={ROUTES.summary.path} element={<SummaryRoute />} />
                <Route
                    path={ROUTES.external_assets.path}
                    element={<AssetsRoot />}
                >
                    <Route
                        path={ROUTES.external_assets.path}
                        element={
                            <Navigate
                                to={
                                    ROUTES.external_assets.children.domains
                                        .path
                                }
                            />
                        }
                    />
                    <Route
                        path={ROUTES.external_assets.children.domains.path}
                        element={<DomainsRoute />}
                    />
                    <Route
                        path={
                            ROUTES.external_assets.children.technologies.path
                        }
                        element={<TechnologiesRoute />}
                    />
                </Route>
                <Route
                    path={ROUTES.users.path}
                    element={<UsersRoute />}
                />
                <Route
                    path={ROUTES.devices.path}
                    element={<DevicesRoute />}
                />
                {fraudDefenseRoute}
                <Route
                    path={ROUTES.integrations.path}
                    element={<IntegrationsRoute />}
                />
                {mdrRoute}
                <Route path={ROUTES.settings.path} element={<SettingsRoute />}>
                    <Route
                        path={ROUTES.settings.path}
                        element={
                            <Navigate
                                to={ROUTES.settings.children.account.path}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.settings.children.account.path}
                        element={<SettingsAccountPage />}
                    />
                    <Route
                        path={ROUTES.settings.children.user.path}
                        element={<SettingsUserRoute />}
                    />
                    <Route
                        path={ROUTES.settings.children.team.path}
                        element={<SettingsTeamRoute />}
                    />
                </Route>
            </Route>
            <Route key={4} path="*" element={<NotFoundRoute />} />
        </Routes>
    )
}

export { MainRouter, ROUTES }
export type TAppRouter = typeof MainRouter
